/**
 * MODULE: _shared/frontend-shell/src/components/generative-ui/FormCard.tsx
 * PURPOSE: Inline form for crud_create (opportunity, contact, etc.) and assistant form-catalog entities (sequence, email_template, presentation, …)
 *
 * AGENT SAFETY: 🟢 SAFE
 * FILE SIZE: 281 / 300 lines
 * Recent: 2026-07-15 [2.2.1586] — **`action: delegate`** → submit **Delegate** (Assign task FormCard).
 * Recent: 2026-04-04 [2.2.292] — Docs sync; heading **`Create ${prettyEntityLabel(entity)}`** used by Playwright **`assistant.spec`** form-catalog mocks. [2.2.277] — default submit **`entity`** from **`formRequest.entity`** when **`continuation`** omits it (root **`CHANGELOG`**).
 */

import { useState } from 'react';
import type { FormRequest, FormField } from '../../hooks/assistant/types';

interface FormCardProps {
  formRequest: FormRequest;
  onSubmit: (data: Record<string, unknown>) => void;
  onCancel: () => void;
  disabled?: boolean;
}

function formatValue(val: unknown): string | number {
  if (val == null) return '';
  return val as string | number;
}

function prettyEntityLabel(entity: string): string {
  const e = (entity || '').trim().toLowerCase();
  if (e === 'opportunity') return 'Opportunity';
  if (!e) return 'Record';
  return e
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

export function FormCard({ formRequest, onSubmit, onCancel, disabled = false }: FormCardProps) {
  const [formData, setFormData] = useState<Record<string, unknown>>(() => {
    const initial: Record<string, unknown> = {};
    formRequest.fields.forEach((field) => {
      if (field.default != null) initial[field.name] = field.default;
    });
    return initial;
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (name: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    formRequest.fields.forEach((field) => {
      if (field.required) {
        const val = formData[field.name];
        if (val == null || val === '' || (typeof val === 'string' && !val.trim())) {
          newErrors[field.name] = `${field.label} is required`;
        }
      }
      if (field.type === 'email') {
        const val = formData[field.name];
        if (typeof val === 'string' && val.trim() && !val.includes('@')) {
          newErrors[field.name] = 'Please enter a valid email';
        }
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate() || disabled) return;
    onSubmit(formData);
  };

  const renderField = (field: FormField) => {
    const hasError = !!errors[field.name];
    const baseClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${
      hasError ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
    }`;

    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            id={field.name}
            name={field.name}
            required={field.required}
            placeholder={field.placeholder}
            value={formatValue(formData[field.name])}
            onChange={(e) => handleChange(field.name, e.target.value)}
            className={baseClass}
            disabled={disabled}
          />
        );
      case 'number':
        return (
          <div className="relative">
            {field.prefix && (
              <span className="absolute left-3 top-2 text-gray-500 text-sm">{field.prefix}</span>
            )}
            <input
              type="number"
              id={field.name}
              name={field.name}
              required={field.required}
              placeholder={field.placeholder}
              value={formatValue(formData[field.name])}
              onChange={(e) => {
                const v = e.target.value;
                handleChange(field.name, v === '' ? undefined : parseFloat(v));
              }}
              className={`${baseClass} ${field.prefix ? 'pl-7' : ''}`}
              disabled={disabled}
            />
          </div>
        );
      case 'select':
        return (
          <select
            id={field.name}
            name={field.name}
            required={field.required}
            value={formatValue(formData[field.name]) || ''}
            onChange={(e) => handleChange(field.name, e.target.value)}
            className={baseClass}
            disabled={disabled}
          >
            <option value="">Select {field.label}</option>
            {field.options?.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );
      case 'date':
        return (
          <input
            type="date"
            id={field.name}
            name={field.name}
            required={field.required}
            value={formatValue(formData[field.name])}
            onChange={(e) => handleChange(field.name, e.target.value || undefined)}
            className={baseClass}
            disabled={disabled}
          />
        );
      case 'textarea':
        return (
          <textarea
            id={field.name}
            name={field.name}
            required={field.required}
            placeholder={field.placeholder}
            value={formatValue(formData[field.name])}
            onChange={(e) => handleChange(field.name, e.target.value)}
            rows={3}
            className={baseClass}
            disabled={disabled}
          />
        );
      case 'email':
        return (
          <input
            type="email"
            id={field.name}
            name={field.name}
            required={field.required}
            placeholder={field.placeholder}
            value={formatValue(formData[field.name])}
            onChange={(e) => handleChange(field.name, e.target.value)}
            className={baseClass}
            disabled={disabled}
          />
        );
      case 'datetime':
        return (
          <input
            type="datetime-local"
            id={field.name}
            name={field.name}
            required={field.required}
            value={formatValue(formData[field.name])}
            onChange={(e) => handleChange(field.name, e.target.value || undefined)}
            className={baseClass}
            disabled={disabled}
          />
        );
      case 'currency':
        return (
          <div className="relative">
            <span className="absolute left-3 top-2 text-gray-500 text-sm">$</span>
            <input
              type="number"
              id={field.name}
              name={field.name}
              required={field.required}
              placeholder={field.placeholder}
              value={formatValue(formData[field.name])}
              onChange={(e) => {
                const v = e.target.value;
                handleChange(field.name, v === '' ? undefined : parseFloat(v));
              }}
              className={`${baseClass} pl-7`}
              disabled={disabled}
            />
          </div>
        );
      default:
        return null;
    }
  };

  const actionLabel =
    formRequest.action === 'compose'
      ? 'Compose'
      : formRequest.action === 'delegate'
        ? 'Delegate'
        : formRequest.action === 'update' || formRequest.mode === 'edit'
          ? 'Update'
          : 'Create';
  const entityForHeading = formRequest.entityType || formRequest.entity;
  const heading =
    formRequest.title?.trim() ||
    `${actionLabel} ${prettyEntityLabel(entityForHeading)}`;
  const primaryButtonLabel =
    formRequest.action === 'delegate'
      ? 'Delegate'
      : formRequest.mode === 'edit' || formRequest.action === 'update'
        ? 'Save changes'
        : actionLabel;

  return (
    <div className="mt-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 shadow-sm">
      <div className="mb-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{heading}</h3>
        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{formRequest.message}</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        {formRequest.fields.map((field) => (
          <div key={field.name}>
            <label
              htmlFor={field.name}
              className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              {field.label}
              {field.required && <span className="text-red-500 ml-0.5">*</span>}
            </label>
            {renderField(field)}
            {errors[field.name] && (
              <p className="text-red-500 text-xs mt-0.5">{errors[field.name]}</p>
            )}
          </div>
        ))}

        <div className="flex gap-2 pt-2">
          <button
            type="submit"
            disabled={disabled}
            className="flex-1 bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm disabled:opacity-50"
          >
            {primaryButtonLabel}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={disabled}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
